import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { state } from '../store/global';
import {
  getGoal, addContribution, investPreview, investPrepare, investConfirm, resetGoal,
  fmtCop, fmtUsd2, fmtDateLong, horizonLabel, portfolioLabel,
} from '../lib/goals';

const NAVY = '#0A2540';
const BLUE = '#2A59C6';
const GREEN = '#16A34A';
const RED = '#DC2626';
const GOLD = '#C9A961';
const SOFT = '#F9FAFB';

const STATUS = {
  adelantada: { label: 'Vas adelantado', color: GREEN },
  al_dia: { label: 'Vas al dia', color: BLUE },
  atrasada: { label: 'Vas atrasado', color: RED },
  cumplida: { label: 'Meta cumplida', color: GREEN },
};

function Stat({ label, value, sub }) {
  return (
    <div className="px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-0.5">{label}</div>
      <div className="text-sm font-semibold text-[#181D27]">{value}</div>
      {sub && <div className="text-xs text-[#6B7280] mt-0.5">{sub}</div>}
    </div>
  );
}

// Humaniza el adelanto: "12 dias", "5 meses", "1 año y 9 meses".
function fmtAhead(days) {
  const d = Math.abs(Number(days || 0));
  if (d < 45) return `${d} día${d === 1 ? '' : 's'}`;
  if (d < 365) return `${Math.round(d / 30)} meses`;
  const years = Math.floor(d / 365);
  const months = Math.round((d % 365) / 30);
  return months > 0 ? `${years} año${years > 1 ? 's' : ''} y ${months} mes${months === 1 ? '' : 'es'}` : `${years} año${years > 1 ? 's' : ''}`;
}

const SparkIcon = ({ color = '#0A2540', size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill={color} />
  </svg>
);

export default function GoalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const snap = useSnapshot(state);
  const userPhone = snap.user?.phonenumber
    ? `${snap.user.phonenumber.nationality?.area_code || ''}${snap.user.phonenumber.number || ''}`
    : '';
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [celebrate, setCelebrate] = useState(null);
  const [invPreview, setInvPreview] = useState(null);
  const [investStep, setInvestStep] = useState('idle'); // idle | sms
  const [investToken, setInvestToken] = useState(null);
  const [smsCode, setSmsCode] = useState('');

  // Vista previa de como se invertiria el aporte (opcion B), al escribir el monto.
  useEffect(() => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { setInvPreview(null); return; }
    let alive = true;
    const t = setTimeout(() => {
      investPreview(id, amt).then((pv) => alive && setInvPreview(pv)).catch(() => alive && setInvPreview(null));
    }, 500);
    return () => { alive = false; clearTimeout(t); };
  }, [amount, id]);

  useEffect(() => {
    let alive = true;
    getGoal(id)
      .then((g) => alive && setGoal(g))
      .catch((e) => alive && setErr(e.response?.data?.message || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  const handleAporte = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return;
    setSaving(true);
    setErr(null);
    try {
      const res = await addContribution(id, { amountCop: amt });
      setGoal(res.data);
      setAmount('');
      if (res.newlyReached?.length) setCelebrate(`Llegaste al ${Math.max(...res.newlyReached)}% de tu meta`);
      else setCelebrate('Aporte registrado. Vas avanzando.');
      setTimeout(() => setCelebrate(null), 4500);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  // Aportar e invertir (opcion B): prepara + envia SMS.
  const handleInvest = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return;
    if (!userPhone) { setErr('No tienes telefono registrado para verificar. Actualizalo en tu perfil.'); return; }
    setSaving(true);
    setErr(null);
    try {
      const res = await investPrepare(id, amt, userPhone);
      setInvestToken(res.token);
      setInvestStep('sms');
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  // Confirma el codigo SMS y ejecuta la inversion.
  const handleConfirmInvest = async () => {
    if (!smsCode || smsCode.length < 4) return;
    setSaving(true);
    setErr(null);
    const oldDaysAhead = goal?.projection?.daysAhead ?? null;
    try {
      const res = await investConfirm(id, investToken, smsCode, userPhone);
      setGoal(res.data);
      setAmount('');
      setSmsCode('');
      setInvestToken(null);
      setInvestStep('idle');
      setInvPreview(null);
      // "+X dias" — cuanto adelanto la meta ESTE aporte (recompensa estilo juego)
      const newDaysAhead = res.data?.projection?.daysAhead ?? null;
      let adelanto = '';
      if (oldDaysAhead != null && newDaysAhead != null && newDaysAhead - oldDaysAhead >= 1) {
        adelanto = ` Adelantaste tu meta ${fmtAhead(newDaysAhead - oldDaysAhead)}.`;
      }
      const reached = res.newlyReached?.length ? ` Llegaste al ${Math.max(...res.newlyReached)}%.` : '';
      setCelebrate(`Invertiste ${fmtUsd2(res.execution?.investedUsd)} en tu portafolio.${adelanto}${reached}`);
      setTimeout(() => setCelebrate(null), 6000);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  const cancelInvest = () => {
    setInvestStep('idle');
    setInvestToken(null);
    setSmsCode('');
    setErr(null);
  };

  // Empezar de nuevo: vuelve la meta a cero (con confirmacion simple).
  const handleReset = async () => {
    if (!window.confirm('¿Quieres empezar tu meta de nuevo? Vuelve a $0 y borra el progreso. (No vende inversiones que ya hiciste.)')) return;
    setSaving(true);
    setErr(null);
    try {
      const g = await resetGoal(id);
      setGoal(g);
      setAmount('');
      setSmsCode('');
      setInvestStep('idle');
      setInvestToken(null);
      setInvPreview(null);
      setCelebrate('Tu meta empezó de nuevo. ¡A darle!');
      setTimeout(() => setCelebrate(null), 3500);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-4 sm:px-10 py-8 text-sm text-[#6B7280]">Cargando tu meta...</div>;
  if (err && !goal) return (
    <div className="px-4 sm:px-10 py-8">
      <div className="text-sm text-red-600 mb-3">No pudimos cargar la meta: {err}</div>
      <button onClick={() => navigate('/dashboard/metas')} className="text-sm underline text-[#0A2540]">Ver mis metas</button>
    </div>
  );

  const p = goal.projection || {};
  const t = Math.max(Number(p.targetCop || 0), 1);
  const cPct = Math.min((Number(p.contributedCop || 0) / t) * 100, 100);
  const mPct = Math.min((Math.max(Number(p.marketGainsCop || 0), 0) / t) * 100, 100 - cPct);
  const st = STATUS[p.statusLabel] || STATUS.al_dia;
  const daysAhead = p.daysAhead;
  const milestones = [25, 50, 75, 100];

  return (
    <div className="px-4 sm:px-10 py-6 max-w-3xl mx-auto" style={{ background: SOFT }}>
      <style>{`
        @keyframes goal-pop {
          0% { opacity: 0; transform: scale(0.96) translateY(-4px); }
          50% { transform: scale(1.015); box-shadow: 0 8px 24px rgba(22,163,74,0.35); }
          100% { opacity: 1; transform: scale(1) translateY(0); box-shadow: 0 2px 8px rgba(22,163,74,0.15); }
        }
        .goal-pop { animation: goal-pop 0.45s cubic-bezier(0.16,1,0.3,1); }
      `}</style>
      <button onClick={() => navigate('/dashboard/metas')} className="text-xs text-[#6B7280] hover:underline mb-4">
        ← Mis Metas
      </button>

      {celebrate && (
        <div className="goal-pop rounded-lg px-4 py-3 mb-4 text-sm font-semibold text-white flex items-center gap-2" style={{ background: GREEN }}>
          <SparkIcon color="#FFFFFF" size={15} />
          {celebrate}
        </div>
      )}

      {/* HERO */}
      <div className="rounded-xl px-6 py-6 mb-4 text-white" style={{ background: NAVY }}>
        <div className="flex items-center gap-2 mb-3">
          {goal.icon && <span className="text-2xl leading-none">{goal.icon}</span>}
          <div>
            <div className="text-base font-semibold">{goal.name}</div>
            <div className="text-xs opacity-70">{horizonLabel(p.horizon)} · objetivo {fmtDateLong(goal.targetDate)}</div>
          </div>
        </div>

        <div className="flex items-end justify-between mb-2">
          <div className="text-3xl font-bold">{Math.round(p.progressPct || 0)}%</div>
          <div className="text-sm opacity-80">{fmtCop(p.currentCop)} de {fmtCop(p.targetCop)}</div>
        </div>

        {/* Barra de dos colores */}
        <div className="flex h-3 rounded-full overflow-hidden bg-white/15 mb-3">
          <div style={{ width: `${cPct}%`, background: '#7FA0FF' }} />
          <div style={{ width: `${mPct}%`, background: '#4ADE80' }} />
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#7FA0FF' }} />
            Tú aportaste {fmtCop(p.contributedCop)}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#4ADE80' }} />
            El mercado sumó {fmtCop(p.marketGainsCop)}{p.marketEstimated ? ' (est.)' : ''}
          </span>
        </div>

        {daysAhead != null && p.statusLabel !== 'cumplida' && (
          daysAhead >= 1 ? (
            <div className="mt-4">
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold"
                style={{ background: 'rgba(201,169,97,0.20)', color: '#F2DEAE' }}>
                <SparkIcon color="#F2DEAE" />
                Llegas {fmtAhead(daysAhead)} antes de lo planeado
              </div>
              <div className="text-[11px] text-white/60 mt-1.5">
                Tus aportes y el mercado te adelantan el sueño. Sigue así.
              </div>
            </div>
          ) : (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium" style={{ background: 'rgba(255,255,255,0.12)' }}>
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: st.color }} />
              {daysAhead <= -15 ? `Vas ${fmtAhead(daysAhead)} atrasado` : 'Vas al día'}
            </div>
          )
        )}
      </div>

      {/* COMPARTIR POR WHATSAPP */}
      <a
        href={`https://wa.me/?text=${encodeURIComponent(
          `Mi meta en FlowexPay: ${goal.name}\n` +
          `Objetivo: ${fmtCop(p.targetCop)} para ${fmtDateLong(goal.targetDate)}\n` +
          `Voy en ${Math.round(p.progressPct || 0)}% y para llegar ahorro ${fmtCop(p.requiredMonthlyCop)}/mes.\n` +
          `Mi dinero ahorra conmigo.`
        )}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-white text-sm font-medium mb-4"
        style={{ background: '#25D366' }}
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.207zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>
        Enviar mi meta por WhatsApp
      </a>

      {/* EL MERCADO PAGO TU CUOTA */}
      {p.marketInstallments >= 1 && (
        <div className="rounded-xl px-5 py-4 mb-4 border" style={{ borderColor: GOLD, background: '#FCFAF3' }}>
          <div className="text-sm font-semibold text-[#181D27]">
            El mercado ya puso {p.marketInstallments} {p.marketInstallments === 1 ? 'cuota' : 'cuotas'} de tu meta
          </div>
          <div className="text-xs text-[#6B7280] mt-0.5">
            No estás ahorrando solo: tu dinero también trabaja por ti{p.marketEstimated ? ' (rendimiento estimado)' : ''}.
          </div>
        </div>
      )}

      {/* QUE TANTO AHORRAR + DATOS */}
      <div className="bg-white border border-[#EFF1F4] rounded-xl mb-4">
        <div className="px-5 py-4 border-b border-[#EFF1F4]">
          <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-1">Para llegar a tiempo</div>
          <div className="text-xl font-bold text-[#181D27]">{fmtCop(p.requiredMonthlyCop)} <span className="text-sm font-normal text-[#6B7280]">/ mes</span></div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-[#EFF1F4]">
          <Stat label="Fecha objetivo" value={fmtDateLong(goal.targetDate)} />
          <Stat
            label="Vas a llegar"
            value={p.projectedFinishDate ? fmtDateLong(p.projectedFinishDate) : '—'}
            sub={daysAhead >= 1 ? <span style={{ color: GREEN, fontWeight: 600 }}>{fmtAhead(daysAhead)} antes</span> : null}
          />
          <Stat label="Portafolio" value={portfolioLabel(p.portfolioType)} sub={`${p.annualReturnPct}% anual est.`} />
          <Stat label="Racha" value={`${p.streakMonths || 0} ${(p.streakMonths || 0) === 1 ? 'mes' : 'meses'}`} sub="aportando" />
        </div>
      </div>

      {/* HITOS */}
      <div className="bg-white border border-[#EFF1F4] rounded-xl px-5 py-4 mb-4">
        <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-3">Tu camino</div>
        <div className="flex items-center justify-between">
          {milestones.map((m) => {
            const reached = (p.progressPct || 0) >= m;
            return (
              <div key={m} className="flex flex-col items-center gap-1">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold border-2"
                  style={reached
                    ? { background: GREEN, borderColor: GREEN, color: '#fff' }
                    : { borderColor: '#EFF1F4', color: '#9CA3AF' }}
                >
                  {m}%
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* APORTAR A LA META */}
      {p.statusLabel !== 'cumplida' && (
        <div className="bg-white border border-[#EFF1F4] rounded-xl px-5 py-4 mb-4">
          <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-2">Aportar a tu meta</div>

          {investStep === 'sms' ? (
            <div>
              <div className="text-sm text-[#181D27] mb-1">Te enviamos un código por SMS para aprobar la inversión.</div>
              <div className="text-xs text-[#6B7280] mb-3">Usa el <b>último</b> código que te llegue. Vence en <b>10 minutos</b>.</div>
              <div className="flex gap-2">
                <input
                  type="text" inputMode="numeric" value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="Código SMS"
                  className="flex-1 border border-[#EFF1F4] rounded-lg px-3 py-2 text-sm text-[#181D27] focus:outline-none focus:border-[#2A59C6]"
                />
                <button
                  onClick={handleConfirmInvest}
                  disabled={saving || smsCode.length < 4}
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-60"
                  style={{ background: NAVY }}
                >
                  {saving ? '...' : 'Confirmar'}
                </button>
              </div>
              <div className="flex items-center gap-4 mt-2">
                <button onClick={cancelInvest} className="text-xs text-[#6B7280] hover:underline">Cancelar</button>
                <button onClick={handleInvest} disabled={saving} className="text-xs text-[#2A59C6] hover:underline disabled:opacity-50">
                  ¿No te llegó? Reenviar código
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <input
                  type="number"
                  inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`${Math.round(p.requiredMonthlyCop || 0)}`}
                  className="flex-1 border border-[#EFF1F4] rounded-lg px-3 py-2 text-sm text-[#181D27] focus:outline-none focus:border-[#2A59C6]"
                />
                <button
                  onClick={handleInvest}
                  disabled={saving || !Number(amount) || !invPreview || invPreview.belowMin || !invPreview.enoughCash}
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-60 whitespace-nowrap"
                  style={{ background: NAVY }}
                >
                  {saving ? '...' : 'Aportar e invertir'}
                </button>
              </div>

              {/* Vista previa (opcion B, dirigido a rebalanceo) */}
              {invPreview && (
                invPreview.belowMin ? (
                  <div className="text-xs text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-3 py-2 mt-3">
                    El mínimo para invertir es ~{fmtCop(invPreview.minCop)} (alcanza para un activo). Sube el monto.
                  </div>
                ) : (
                  <div className="mt-3 rounded-lg border border-[#EFF1F4] bg-[#F9FAFB] px-3 py-3">
                    <div className="text-[11px] text-[#6B7280] mb-2">
                      Invertiríamos <b>{fmtUsd2(invPreview.totalInvestUsd)} USD</b> (de {fmtCop(invPreview.amountCop)}) en tu portafolio {portfolioLabel(invPreview.portfolioType)}:
                    </div>
                    {invPreview.legs.map((l) => (
                      <div key={l.symbol} className="flex items-center justify-between text-xs py-0.5">
                        <span className="text-[#181D27] font-medium">{l.symbol}</span>
                        <span className="text-[#6B7280]">{fmtUsd2(l.amountUsd)} <span className="text-[#9CA3AF]">· comisión {fmtUsd2(l.feeTotalUsd)}</span></span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-xs font-semibold text-[#181D27] mt-1 pt-1 border-t border-[#EFF1F4]">
                      <span>Comisión total · Total a debitar</span>
                      <span>{fmtUsd2(invPreview.totalFeeUsd)} · {fmtUsd2(invPreview.totalDebitUsd)}</span>
                    </div>
                    {!invPreview.enoughCash && (
                      <div className="text-[11px] text-[#92400E] mt-2">Tu efectivo en el broker ({fmtUsd2(invPreview.brokerCashUsd)}) no alcanza para este aporte.</div>
                    )}
                    <div className="text-[10px] text-[#9CA3AF] mt-2">Al aportar e invertir te pediremos un código por SMS para aprobar la compra.</div>
                  </div>
                )
              )}

              {invPreview && !invPreview.belowMin && !invPreview.enoughCash && (
                <div className="text-[11px] text-[#92400E] mt-2">No hay efectivo suficiente en el broker para invertir este aporte.</div>
              )}
            </>
          )}

          {err && <div className="text-xs text-red-600 mt-2">{err}</div>}
        </div>
      )}

      <div className="text-[11px] text-[#9CA3AF] px-1">
        Proyección estimada con rentabilidad conservadora ({p.annualReturnPct}% anual). Es un estimado, no una promesa de rendimiento. Invertir tiene riesgos.
      </div>

      <div className="text-center mt-4">
        <button onClick={handleReset} disabled={saving} className="text-xs text-[#9CA3AF] hover:text-[#DC2626] hover:underline disabled:opacity-50">
          Empezar esta meta de nuevo
        </button>
      </div>
    </div>
  );
}
